import React from "react";
import { cn } from "@/utils/shadcn";
import { LAB } from "./tokens";
import { Lab } from "./atoms";

/* ── Chips · filter row ─────────────────────────────────────────────── */

export const Chips = <T,>({
  items,
  value,
  onChange,
  className,
}: {
  items: Array<{ label: React.ReactNode; value: T }>;
  value: T;
  onChange: (value: T) => void;
  className?: string;
}) => (
  <div className={cn("flex flex-wrap gap-[7px]", className)}>
    {items.map((it, i) => {
      const on = it.value === value;
      return (
        <button
          key={i}
          type="button"
          onClick={() => onChange(it.value)}
          className={cn(
            LAB,
            "inline-flex min-h-[36px] items-center border px-3 text-[9.5px] transition-colors",
            on
              ? "border-ms-ink bg-ms-ink text-ms-bg"
              : "border-ms-line text-ms-mute hover:border-ms-line2 hover:text-ms-ink"
          )}
        >
          {it.label}
        </button>
      );
    })}
  </div>
);

/* ── SearchBar ──────────────────────────────────────────────────────── */

export const SearchBar = ({
  value,
  onChange,
  placeholder = "Search",
  className,
  ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">) => (
  <label
    className={cn(
      "flex min-h-[44px] items-center gap-2.5 border border-ms-line px-3.5 text-[13.5px] text-ms-mute focus-within:border-ms-line2",
      className
    )}
  >
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" className="flex-none">
      <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 12l3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
    <input
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="min-w-0 flex-1 bg-transparent text-ms-ink outline-none placeholder:text-ms-mute"
    />
  </label>
);

/* ── KindSwitch · the API / CSV / Address tiles ─────────────────────── */

export const KindSwitch = <T,>({
  items,
  value,
  onChange,
  className,
}: {
  items: Array<{ label: React.ReactNode; value: T; icon?: React.ReactNode }>;
  value: T;
  onChange: (value: T) => void;
  className?: string;
}) => (
  <div className={cn("grid grid-cols-3 gap-2.5", className)}>
    {items.map((it, i) => {
      const on = it.value === value;
      return (
        <button
          key={i}
          type="button"
          onClick={() => onChange(it.value)}
          className={cn(
            "flex min-h-[70px] flex-col items-center justify-center gap-[7px] border border-t-[3px] transition-colors",
            on
              ? "border-ms-line border-t-ms-rule bg-ms-card2 text-ms-ink"
              : "border-ms-line border-t-transparent text-ms-dim hover:text-ms-ink"
          )}
        >
          {it.icon}
          <Lab size={9} className={on ? "text-ms-ink" : undefined}>
            {it.label}
          </Lab>
        </button>
      );
    })}
  </div>
);

/* ── Field + KeyBox · the credential inputs ─────────────────────────── */

export const Field = ({
  label,
  hint,
  children,
  className,
}: {
  label: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("flex flex-col gap-2", className)}>
    <div className="flex items-baseline justify-between gap-3">
      <Lab className="text-ms-ink">{label}</Lab>
      {hint ? <Lab className="text-ms-dim">{hint}</Lab> : null}
    </div>
    {children}
  </div>
);

const EyeIcon = ({ off }: { off?: boolean }) => (
  <svg width="17" height="13" viewBox="0 0 16 12" fill="none">
    <path d="M1 6s2.5-4.5 7-4.5S15 6 15 6s-2.5 4.5-7 4.5S1 6 1 6z" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="8" cy="6" r="1.9" stroke="currentColor" strokeWidth="1.4" />
    {off ? <path d="M2 2l12 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /> : null}
  </svg>
);

export const KeyBox = ({
  value,
  onChange,
  placeholder,
  /** turns the 3px left rule green — the "we accepted this" signal */
  ok,
  secret,
  onPaste,
  className,
  ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ok?: boolean;
  secret?: boolean;
  /** shows a Paste chip; omit to hide it */
  onPaste?: () => void;
  className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "onPaste">) => {
  const [revealed, setRevealed] = React.useState(false);
  return (
    <div
      className={cn(
        "flex min-h-[50px] items-center gap-2 border border-l-[3px] border-ms-line py-0 pl-3.5 pr-2",
        ok ? "border-l-ms-rule" : "border-l-ms-line",
        className
      )}
    >
      <input
        {...rest}
        type={secret && !revealed ? "password" : "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent font-mono text-[14px] tracking-[0.1em] text-ms-ink outline-none placeholder:tracking-normal placeholder:text-ms-mute"
      />
      {secret ? (
        <button
          type="button"
          aria-label={revealed ? "Hide" : "Reveal"}
          onClick={() => setRevealed((r) => !r)}
          className="inline-flex min-h-[40px] min-w-[40px] items-center justify-center text-ms-mute transition-colors hover:text-ms-ink"
        >
          <EyeIcon off={!revealed} />
        </button>
      ) : null}
      {onPaste ? (
        <button
          type="button"
          onClick={onPaste}
          className={cn(
            LAB,
            "min-h-[40px] border border-ms-line bg-ms-chip px-3.5 text-[9.5px] text-ms-mute transition-colors hover:text-ms-ink"
          )}
        >
          Paste
        </button>
      ) : null}
    </div>
  );
};

/* ── Drop · the CSV dropzone ────────────────────────────────────────── */

export const Drop = ({
  title,
  hint,
  cta = "Choose file",
  accept,
  onFiles,
  children,
  className,
}: {
  title: React.ReactNode;
  hint?: React.ReactNode;
  cta?: React.ReactNode;
  accept?: string;
  onFiles?: (files: FileList) => void;
  /** slot under the CTA — the accepted-file confirmation lives here */
  children?: React.ReactNode;
  className?: string;
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (onFiles && e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
      }}
      className={cn("border border-dashed border-ms-line2 px-5 py-8 text-center", className)}
    >
      <div className="mx-auto grid h-[46px] w-[46px] place-items-center bg-ms-chip text-ms-mute">
        <svg width="22" height="22" viewBox="0 0 18 18" fill="none">
          <rect x="4" y="2.5" width="10" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M6.6 6.5h4.8M6.6 9.5h4.8M6.6 12.5h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </div>
      <div className="mt-3.5 text-[16.5px] font-semibold tracking-[-0.025em]">{title}</div>
      {hint ? (
        <div className="mt-[7px] text-[13px] leading-[1.45] text-ms-mute">{hint}</div>
      ) : null}
      {onFiles ? (
        <div className="mt-4">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => e.target.files?.length && onFiles(e.target.files)}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={cn(
              LAB,
              "inline-flex min-h-[40px] items-center border border-ms-line bg-ms-chip px-4 text-[9.5px] text-ms-mute transition-colors hover:text-ms-ink"
            )}
          >
            {cta}
          </button>
        </div>
      ) : null}
      {children ? <div className="mt-[18px]">{children}</div> : null}
    </div>
  );
};
